
import React, { useState, useEffect } from 'react';
import {
    Building2, Ship, Truck, ArrowRight, ArrowLeft, Check,
    Package, FileText, MapPin, DollarSign, Users, Zap,
    Shield, Clock, BarChart3, Bot, FileCheck, Calculator,
    Loader2, CreditCard, Tag, AlertCircle, CheckCircle2, X,
    Eye,
    EyeOff
} from 'lucide-react';
import axios from 'axios';
import { ValidateInput } from '../SearchBox/ValidateInput';
import { validateCompanyName, validateEmail, validateUsername } from '@/utils/validators';
import { useDispatch, useSelector } from 'react-redux';
import { APICall } from '@/config/NetworkUtility';
import { callLoader } from '@/redux/Loader/actions';
import { getModuleDetails } from '@/redux/Modules/actions';



const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Company type options
const COMPANY_TYPES = [
    {
        id: 'BCO',
        name: 'Beneficial Cargo Owner',
        description: 'Shipper or consignee who owns the cargo',
        icon: Package,
        color: 'from-blue-500 to-blue-600'
    },
    {
        id: 'NVOCC',
        name: 'NVOCC',
        description: 'Non-Vessel Operating Common Carrier',
        icon: Ship,
        color: 'from-teal-500 to-teal-600'
    },
    {
        id: 'FreightForwarder',
        name: 'Freight Forwarder',
        description: 'Logistics service provider',
        icon: Truck,
        color: 'from-purple-500 to-purple-600'
    }
];

// Module icons mapping
const MODULE_ICONS = {
    CORE_TMS: Ship,
    RATE_QUOTE: Calculator,
    BOOKING: FileText,
    TRACKING: MapPin,
    DOCUMENTATION: FileCheck,
    ACCOUNTING: DollarSign,
    CONTROL_TOWER: BarChart3,
    AI_AGENTS: Bot,
    CUSTOMS: Shield,
    REPORTING: BarChart3
};

const CompanyWizard = ({ isOpen, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState({ core_modules: [], addon_modules: [], all_modules: [] });
    const [error, setError] = useState('');
    const countryList = useSelector(
        (state) => state.common.countryList
    );

    const [stateList, setStateList] =
        useState([]);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordError, setPasswordError] = useState("");

    console.log("countryList:", countryList);

    // Form data
    const [formData, setFormData] = useState({
        // =========================
        // Step 1 : Company Type
        // =========================
        profileCategory: "",

        // =========================
        // Company Information
        // =========================
        companyId: 0,
        companyName: "",
        companyLogo: "",
        POWERED_BY: "shipez.ai",

        // =========================
        // Profile / Company Details
        // =========================
        profileFirstName: "",
        profileLastName: "",
        profileEmailId: "",
        profileUsername: "",
        profilePassword: "",
        confirmPassword: "",

        profilePhone: "",
        profileCountryCode: "+1",

        profileAddress1: "",
        profileAddress2: "",
        profileAddressPrint: "",

        profileCity: "",
        profileRegion: "",
        profileCountry: "USA",
        profileZipCode: "",

        nvocc_license: "",

        // =========================
        // Subscription / Status
        // =========================
        subscription_status: "active",

        // =========================
        // File Upload
        // =========================
        FileData: null,

        // =========================
        // Action Info
        // =========================
        actionBy: "admin",

        // =========================
        // Selected Modules
        // =========================
        selected_modules: {},

        /*
            Structure Example:
    
            {
                1: {
                    ID: 0,
                    BASEMODULE_ID: 1,
                    COMPANY_ID: 0,
                    SEATS: 5,
                    CUSTOM_PRICE: 200,
                    SUBSCRIBED_BY: "admin",
    
                    modules: {
                        1: {
                            ID: 0,
                            MODULE_ID: 1,
                            COMPANY_ID: 0,
                            SEATS: 5,
                            CUSTOM_PRICE: 100,
                            SUBSCRIBED_BY: "admin",
    
                            subModules: {
                                1: {
                                    ID: 0,
                                    SUBMODULE_ID: 1,
                                    COMPANY_ID: 0,
                                    SEATS: 5,
                                    CUSTOM_PRICE: 100,
                                    SUBSCRIBED_BY: "admin",
                                }
                            }
                        }
                    }
                }
            }
        */

        // =========================
        // Final API Payload
        // =========================
        subscribedBaseModuleDetailsJson: "",
    });

    console.log('formData: ', formData);



    // Fetch modules on mount
    useEffect(() => {
        if (isOpen) {
            fetchModules();
            // Reset form when opened
            setCurrentStep(1);
            setError('');
            setFormData({
                // =========================
                // Step 1 : Company Type
                // =========================
                profileCategory: "",

                // =========================
                // Company Information
                // =========================
                companyId: 0,
                companyName: "",
                companyLogo: "",
                POWERED_BY: "shipez.ai",

                // =========================
                // Profile / Company Details
                // =========================
                profileFirstName: "",
                profileLastName: "",
                profileEmailId: "",
                profileUsername: "",
                profilePassword: "",
                confirmPassword: "",

                profilePhone: "",
                profileCountryCode: "+1",

                profileAddress1: "",
                profileAddress2: "",
                profileAddressPrint: "",

                profileCity: "",
                profileRegion: "",
                profileCountry: "USA",
                profileZipCode: "",

                nvocc_license: "",

                // =========================
                // Subscription / Status
                // =========================
                subscription_status: "active",

                // =========================
                // File Upload
                // =========================
                FileData: null,

                // =========================
                // Action Info
                // =========================
                actionBy: "admin",

                // =========================
                // Selected Modules
                // =========================
                selected_modules: {},

                /*
                    Structure Example:
            
                    {
                        1: {
                            ID: 0,
                            BASEMODULE_ID: 1,
                            COMPANY_ID: 0,
                            SEATS: 5,
                            CUSTOM_PRICE: 200,
                            SUBSCRIBED_BY: "admin",
            
                            modules: {
                                1: {
                                    ID: 0,
                                    MODULE_ID: 1,
                                    COMPANY_ID: 0,
                                    SEATS: 5,
                                    CUSTOM_PRICE: 100,
                                    SUBSCRIBED_BY: "admin",
            
                                    subModules: {
                                        1: {
                                            ID: 0,
                                            SUBMODULE_ID: 1,
                                            COMPANY_ID: 0,
                                            SEATS: 5,
                                            CUSTOM_PRICE: 100,
                                            SUBSCRIBED_BY: "admin",
                                        }
                                    }
                                }
                            }
                        }
                    }
                */

                // =========================
                // Final API Payload
                // =========================
                subscribedBaseModuleDetailsJson: "",
            });
        }
    }, [isOpen]);

    const fetchModules = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/payments/modules/pricing`);
            if (res.data.success) {
                setModules(res.data);
            }
        } catch (err) {
            console.error('Error fetching modules:', err);
        }
    };

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    // const toggleModule = (moduleCode) => {
    //     setFormData(prev => {
    //         const selected = prev.selected_modules.includes(moduleCode)
    //             ? prev.selected_modules.filter(m => m !== moduleCode)
    //             : [...prev.selected_modules, moduleCode];
    //         // Always include CORE_TMS
    //         if (!selected.includes('CORE_TMS')) {
    //             selected.push('CORE_TMS');
    //         }
    //         return { ...prev, selected_modules: selected };
    //     });
    // };

    const createDefaultSubModule = (sub) => ({
        ID: 0,
        SUBMODULE_ID: sub.subModuleId,
        COMPANY_ID: 0,
        SEATS: 5,
        CUSTOM_PRICE: sub.subModuleBasePrice || 0,
        SUBSCRIBED_BY: "super_admin",
    });

    const createDefaultModule = (module) => ({
        ID: 0,
        MODULE_ID: module.moduleId,
        COMPANY_ID: 0,
        SEATS: 5,
        CUSTOM_PRICE: module.moduleBasePrice || 0,
        SUBSCRIBED_BY: "super_admin",
        subModules: {},
    });

    const createDefaultBaseModule = (baseModule) => ({
        ID: 0,
        BASEMODULE_ID: baseModule.baseModuleId,
        COMPANY_ID: 0,
        SEATS: 5,
        CUSTOM_PRICE: baseModule.baseModuleBasePrice || 0,
        SUBSCRIBED_BY: "super_admin",
        modules: {},
    });

    const toggleBaseModule = (baseModule) => {
        setFormData((prev) => {
            const updated = structuredClone(
                prev.selected_modules || {}
            );

            const exists =
                updated[baseModule.baseModuleId];

            if (exists) {
                delete updated[baseModule.baseModuleId];
            } else {
                updated[baseModule.baseModuleId] =
                    createDefaultBaseModule(baseModule);

                // auto select all child modules for CORE
                if (
                    baseModule.baseModuleType === "CORE"
                ) {
                    baseModule.modulesData?.forEach(
                        (mod) => {
                            updated[
                                baseModule.baseModuleId
                            ].modules[mod.moduleId] =
                                createDefaultModule(mod);

                            mod.subModulesData?.forEach(
                                (sub) => {
                                    updated[
                                        baseModule.baseModuleId
                                    ].modules[
                                        mod.moduleId
                                    ].subModules[
                                        sub.subModuleId
                                    ] =
                                        createDefaultSubModule(
                                            sub
                                        );
                                }
                            );
                        }
                    );
                }
            }

            return {
                ...prev,
                selected_modules: updated,
            };
        });
    };

    const toggleChildModule = (
        baseModuleId,
        moduleData
    ) => {
        setFormData((prev) => {
            const updated = structuredClone(
                prev.selected_modules
            );

            const exists =
                updated?.[baseModuleId]?.modules?.[
                moduleData.moduleId
                ];

            if (exists) {
                delete updated[baseModuleId].modules[
                    moduleData.moduleId
                ];
            } else {
                updated[baseModuleId].modules[
                    moduleData.moduleId
                ] = createDefaultModule(moduleData);

                moduleData.subModulesData?.forEach(
                    (sub) => {
                        updated[
                            baseModuleId
                        ].modules[
                            moduleData.moduleId
                        ].subModules[sub.subModuleId] =
                            createDefaultSubModule(sub);
                    }
                );
            }

            return {
                ...prev,
                selected_modules: updated,
            };
        });
    };

    const toggleSubModule = (
        baseModuleId,
        moduleId,
        subModule
    ) => {
        setFormData((prev) => {
            const updated = structuredClone(
                prev.selected_modules
            );

            const exists =
                updated?.[baseModuleId]
                    ?.modules?.[moduleId]
                    ?.subModules?.[
                subModule.subModuleId
                ];

            if (exists) {
                delete updated[baseModuleId]
                    .modules[moduleId]
                    .subModules[
                    subModule.subModuleId
                ];
            } else {
                updated[baseModuleId]
                    .modules[moduleId]
                    .subModules[
                    subModule.subModuleId
                ] =
                    createDefaultSubModule(subModule);
            }

            return {
                ...prev,
                selected_modules: updated,
            };
        });
    };

    const toggleAllChildModules = (baseModule) => {
        setFormData((prev) => {
            const selectedModules = {
                ...(prev.selected_modules || {}),
            };

            const baseId = baseModule.baseModuleId;

            // if already all selected -> unselect all child modules
            const existingModules =
                selectedModules?.[baseId]?.modules || {};

            const allSelected =
                baseModule.modulesData?.every(
                    (child) => existingModules?.[child.moduleId]
                );

            // create base module if not exists
            if (!selectedModules[baseId]) {
                selectedModules[baseId] = {
                    baseModuleId: baseId,
                    baseModuleName: baseModule.baseModuleName,
                    basePrice: baseModule.baseModuleBasePrice,
                    modules: {},
                };
            }

            if (allSelected) {
                // remove all child modules
                selectedModules[baseId].modules = {};
            } else {
                // select all child + submodules
                const allModules = {};

                baseModule.modulesData?.forEach((child) => {
                    const subModules = {};

                    child.subModulesData?.forEach((sub) => {
                        subModules[sub.subModuleId] = {
                            subModuleId: sub.subModuleId,
                            subModuleName: sub.subModuleName,
                            price: sub.subModuleBasePrice,
                        };
                    });

                    allModules[child.moduleId] = {
                        moduleId: child.moduleId,
                        moduleName: child.moduleName,
                        price: child.moduleBasePrice,
                        subModules,
                    };
                });

                selectedModules[baseId].modules = allModules;
            }

            return {
                ...prev,
                selected_modules: selectedModules,
            };
        });
    };

    const toggleAllSubModules = (baseModuleId, childModule) => {
        setFormData((prev) => {
            const selectedModules = {
                ...(prev.selected_modules || {}),
            };

            // ensure base module exists
            if (!selectedModules[baseModuleId]) {
                selectedModules[baseModuleId] = {
                    modules: {},
                };
            }

            // ensure child module exists
            if (!selectedModules[baseModuleId].modules) {
                selectedModules[baseModuleId].modules = {};
            }

            if (
                !selectedModules[baseModuleId].modules[
                childModule.moduleId
                ]
            ) {
                selectedModules[baseModuleId].modules[
                    childModule.moduleId
                ] = {
                    moduleId: childModule.moduleId,
                    moduleName: childModule.moduleName,
                    price: childModule.moduleBasePrice,
                    subModules: {},
                };
            }

            const existingSubModules =
                selectedModules[baseModuleId].modules[
                    childModule.moduleId
                ].subModules || {};

            const allSelected =
                childModule.subModulesData?.every(
                    (sub) => existingSubModules?.[sub.subModuleId]
                );

            if (allSelected) {
                // unselect all submodules
                selectedModules[baseModuleId].modules[
                    childModule.moduleId
                ].subModules = {};
            } else {
                // select all submodules
                const allSubs = {};

                childModule.subModulesData?.forEach((sub) => {
                    allSubs[sub.subModuleId] = {
                        subModuleId: sub.subModuleId,
                        subModuleName: sub.subModuleName,
                        price: sub.subModuleBasePrice,
                    };
                });

                selectedModules[baseModuleId].modules[
                    childModule.moduleId
                ].subModules = allSubs;
            }

            return {
                ...prev,
                selected_modules: selectedModules,
            };
        });
    };


    const calculateTotal = () => {
        let total = 0;

        Object.values(
            formData?.selected_modules || {}
        ).forEach((base) => {
            total += Number(base.CUSTOM_PRICE || 0);

            Object.values(base.modules || {}).forEach(
                (module) => {
                    total += Number(
                        module.CUSTOM_PRICE || 0
                    );

                    Object.values(
                        module.subModules || {}
                    ).forEach((sub) => {
                        total += Number(
                            sub.CUSTOM_PRICE || 0
                        );
                    });
                }
            );
        });

        return total;
    };

    // const calculateTotal = () => {
    //     let total = 0;
    //     formData.selected_modules.forEach(code => {
    //         const module = modules.all_modules.find(m => m.module_code === code);
    //         if (module) {
    //             total += parseFloat(module.base_price || 0);
    //             if (module.pricing_model === 'per_seat') {
    //                 total += parseFloat(module.per_seat_price || 0);
    //             }
    //         }
    //     });
    //     return total;
    // };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.profileCategory) {
                    setError('Please select a company type');
                    return false;
                }
                break;
            case 2:
                // if (!formData.company_name || !formData.email || !formData.admin_username ||
                //     !formData.admin_password || !formData.admin_first_name || !formData.admin_last_name) {
                //     setError('Please fill in all required fields');
                //     return false;
                // }
                // if (formData.admin_password !== formData.admin_confirm_password) {
                //     setError('Passwords do not match');
                //     return false;
                // }
                // if (formData.admin_password.length < 8) {
                //     setError('Password must be at least 8 characters');
                //     return false;
                // }
                // if (formData.company_type === 'NVOCC' && !formData.nvocc_license) {
                //     setError('NVOCC License number is required');
                //     return false;
                // }
                break;
            case 3:
                if (formData.selected_modules.length === 0) {
                    setError('Please select at least one module');
                    return false;
                }
                break;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 4));
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        setError('');
    };



    // =========================
    // CREATE DUMMY PAYLOAD
    // =========================

    const createDummyPayload = async () => {
        const dummyPayload = new FormData();

        dummyPayload.append("companyId", 0);
        dummyPayload.append(
            "companyName",
            "Acme Global Logistics"
        );

        dummyPayload.append("companyLogo", "");
        dummyPayload.append("POWERED_BY", "SHIPEZ.AI");
        dummyPayload.append("actionBy", "super_admin");

        // Company Type
        dummyPayload.append(
            "profileCategory",
            "NVOCC"
        );

        // Admin User
        dummyPayload.append(
            "profileFirstName",
            "John"
        );

        dummyPayload.append(
            "profileLastName",
            "Doe"
        );

        dummyPayload.append(
            "profileUsername",
            `john_${Date.now()}`
        );

        dummyPayload.append(
            "profilePassword",
            "Admin@123"
        );

        // Contact
        dummyPayload.append(
            "profileEmailId",
            `john_${Date.now()}@acmelogistics.com`
        );

        dummyPayload.append(
            "profilePhone",
            "+1-555-123-4567"
        );

        dummyPayload.append(
            "profileCountryCode",
            "+91"
        );

        // Address
        dummyPayload.append(
            "profileAddress1",
            "123 Business Avenue"
        );

        dummyPayload.append(
            "profileAddress2",
            "Suite 401"
        );

        dummyPayload.append(
            "profileAddressPrint",
            "123 Business Avenue, Suite 401"
        );

        dummyPayload.append(
            "profileCity",
            "Los Angeles"
        );

        dummyPayload.append(
            "profileRegion",
            "California"
        );

        dummyPayload.append(
            "profileCountry",
            "USA"
        );

        dummyPayload.append(
            "profileZipCode",
            "90001"
        );

        // =========================
        // PNG FILE
        // =========================

        const imageUrl =
            "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

        const response = await fetch(
            imageUrl
        );

        const blob = await response.blob();

        const pngFile = new File(
            [blob],
            "logo.png",
            {
                type: "image/png",
            }
        );

        dummyPayload.append(
            "FileData",
            pngFile
        );

        // =========================
        // MODULES
        // =========================

        const subscribedBaseModuleDetails = {
            ID: 0,
            BASEMODULE_ID: 1,
            COMPANY_ID: 0,
            SEATS: 5,
            CUSTOM_PRICE: 0,
            SUBSCRIBED_BY: "super_admin",

            subscribedModuleDetails: [
                {
                    ID: 0,
                    MODULE_ID: 101,
                    COMPANY_ID: 0,
                    SEATS: 5,
                    CUSTOM_PRICE: 99,
                    SUBSCRIBED_BY: "super_admin",

                    subscribedSubModuleDetails: [
                        {
                            ID: 0,
                            SUBMODULE_ID: 1001,
                            COMPANY_ID: 0,
                            SEATS: 5,
                            CUSTOM_PRICE: 49,
                            SUBSCRIBED_BY:
                                "super_admin",
                        },
                    ],
                },
                {
                    ID: 0,
                    MODULE_ID: 102,
                    COMPANY_ID: 0,
                    SEATS: 3,
                    CUSTOM_PRICE: 149,
                    SUBSCRIBED_BY: "super_admin",

                    subscribedSubModuleDetails: [],
                },
            ],
        };

        dummyPayload.append(
            "subscribedBaseModuleDetails",
            JSON.stringify(
                subscribedBaseModuleDetails
            )
        );

        dummyPayload.append(
            "subscribedBaseModuleDetailsJson",
            JSON.stringify(
                subscribedBaseModuleDetails
            )
        );

        return dummyPayload;
    };


    const handleCreateCompany = async () => {
        setLoading(true);
        setError("");

        try {
            const payload = new FormData();

            // Basic Company Details
            payload.append("companyId", 0);
            payload.append("companyName", formData.companyName);
            payload.append("companyLogo", "");
            payload.append("POWERED_BY", "SHIPEZ.AI");
            payload.append("actionBy", "super_admin");

            // Company Type
            payload.append("profileCategory", formData.profileCategory);

            // Admin User
            payload.append("profileFirstName", formData.profileFirstName);
            payload.append("profileLastName", formData.profileLastName);
            payload.append("profileUsername", formData.profileUsername);
            payload.append("profilePassword", formData.profilePassword);

            // Contact
            payload.append("profileEmailId", formData.profileEmailId);
            payload.append("profilePhone", formData.profilePhone || "");
            payload.append("profileCountryCode", "+91");

            // Address
            payload.append("profileAddress1", formData.profileAddress1 || "");
            payload.append("profileAddress2", "");
            payload.append(
                "profileAddressPrint",
                `${formData.profileAddress1 || ""}, ${formData.profileCity || ""}`
            );

            payload.append("profileCity", formData.profileCity || "");
            payload.append("profileRegion", formData.profileRegion || "");
            payload.append("profileCountry", formData.profileCountry || "USA");
            payload.append("profileZipCode", formData.profileZipCode || "");

            // Optional file
            if (formData.FileData) {
                payload.append("FileData", formData.FileData);
            }

            // Modules Mapping
            const subscribedBaseModuleDetails = {
                ID: 0,
                BASEMODULE_ID: 1,
                COMPANY_ID: 0,
                SEATS: 1,
                CUSTOM_PRICE: 0,
                SUBSCRIBED_BY: "super_admin",

                subscribedModuleDetails: formData.selected_modules.map(
                    (moduleCode, index) => ({
                        ID: 0,
                        MODULE_ID: index + 1,
                        COMPANY_ID: 0,
                        SEATS: 1,
                        CUSTOM_PRICE: 0,
                        SUBSCRIBED_BY: "super_admin",

                        subscribedSubModuleDetails: [],
                    })
                ),
            };

            payload.append(
                "subscribedBaseModuleDetails",
                JSON.stringify(subscribedBaseModuleDetails)
            );

            payload.append(
                "subscribedBaseModuleDetailsJson",
                JSON.stringify(subscribedBaseModuleDetails)
            );

            // const dummyPayload = await createDummyPayload();
            for (const [key, value] of payload.entries()) {
                if (value instanceof File) {
                    console.log(key, {
                        name: value.name,
                        type: value.type,
                        size: value.size,
                    });
                } else {
                    console.log(key, value);
                }
            }

            // API Call
            const response = await axios.post(
                "https://api.shipez.ai/api/v2/CompanySetup/CompanyRequestRegister",
                payload,
                // dummyPayload,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            console.log("Company Created:", response.data);

            // Success
            onSuccess?.();
            onClose?.();

        } catch (err) {
            console.error("Error creating company:", err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.title ||
                err.message ||
                "Failed to create company"
            );
        } finally {
            setLoading(false);
        }
    };

    // const handleCreateCompany = async () => {
    //     setLoading(true);
    //     setError('');

    //     try {
    //         // Create company with admin user via signup endpoint
    //         const signupRes = await axios.post(`${BACKEND_URL}/api/admin/companies/signup`, {
    //             company_name: formData.company_name,
    //             company_type: formData.company_type,
    //             email: formData.email,
    //             phone: formData.phone,
    //             address: formData.address,
    //             city: formData.city,
    //             state: formData.state,
    //             country: formData.country,
    //             postal_code: formData.postal_code,
    //             website: formData.website,
    //             nvocc_license: formData.nvocc_license,
    //             selected_modules: formData.selected_modules,
    //             admin_email: formData.admin_email,
    //             admin_password: formData.admin_password,
    //             admin_first_name: formData.admin_first_name,
    //             admin_last_name: formData.admin_last_name
    //         });

    //         if (!signupRes.data.success) {
    //             throw new Error(signupRes.data.detail || 'Failed to create company');
    //         }

    //         // If admin set status to active, update it
    //         if (formData.subscription_status === 'active') {
    //             const companyId = signupRes.data.company.id;
    //             await axios.put(`${BACKEND_URL}/api/admin/companies/${companyId}`, {
    //                 subscription_status: 'active'
    //             });

    //             // Activate selected modules
    //             await axios.post(`${BACKEND_URL}/api/admin/companies/${companyId}/modules`, {
    //                 company_id: companyId,
    //                 module_codes: formData.selected_modules,
    //                 seats: 1
    //             });
    //         }

    //         // Success - close wizard and refresh
    //         onSuccess && onSuccess();
    //         onClose();

    //     } catch (err) {
    //         console.error('Error creating company:', err);
    //         setError(err.response?.data?.detail || err.message || 'An error occurred');
    //     }

    //     setLoading(false);
    // };




    const fetchStates = async (
        countryCode
    ) => {
        console.log('countryCode: ', countryCode);

        try {
            const response = await APICall(
                "get",
                {},
                `v1/Common/GetCountryStateList?CountryCode=${countryCode}`
            );

            setStateList(
                response?.data || []
            );
        } catch (err) {
            console.error(
                "State API Error:",
                err
            );
        }
    };

    // Step indicator


    const steps = [
        { num: 1, label: 'Company Type' },
        { num: 2, label: 'Details' },
        { num: 3, label: 'Modules' },
        { num: 4, label: 'Review' }
    ];

    const getModules = async () => {
        try {
            dispatch(callLoader(true))
            const data = await dispatch(getModuleDetails())
            console.log("Modules:", data);
            setModules(data)
        } catch (error) {
            dispatch(callLoader(false))
        } finally {
            dispatch(callLoader(false))

        }
    }


    // Debounce password match check
    useEffect(() => {
        const timer = setTimeout(() => {
            if (
                formData.confirmPassword &&
                formData.profilePassword !== formData.confirmPassword
            ) {
                setPasswordError("Passwords do not match");
            } else {
                setPasswordError("");
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [formData.profilePassword, formData.confirmPassword]);

    useEffect(() => {
        getModules();
    }, [])

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="company-wizard-modal">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">
                        Add New Company
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                    <div className="flex items-center justify-between max-w-2xl mx-auto">
                        {steps.map((step, idx) => (
                            <React.Fragment key={step.num}>
                                <div className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${currentStep >= step.num
                                        ? 'bg-teal-500 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                        }`}>
                                        {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                                    </div>
                                    <span className={`ml-2 text-xs font-medium hidden sm:block ${currentStep >= step.num ? 'text-white' : 'text-slate-500'
                                        }`}>
                                        {step.label}
                                    </span>
                                </div>
                                {idx < steps.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-3 ${currentStep > step.num ? 'bg-teal-500' : 'bg-slate-700'
                                        }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-400 text-sm">{error}</span>
                        </div>
                    )}

                    {/* Step 1: Company Type */}
                    {currentStep === 1 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">Select Company Type</h3>
                            <p className="text-slate-400 text-sm mb-6">Choose the type that best describes the business</p>

                            <div className="grid md:grid-cols-3 gap-4">
                                {COMPANY_TYPES.map(type => (
                                    <div
                                        key={type.id}
                                        onClick={() => updateFormData('profileCategory', type.id)}
                                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${formData.profileCategory === type.id
                                            ? 'border-teal-500 bg-teal-500/10'
                                            : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                                            }`}
                                    >
                                        {formData.profileCategory === type.id && (
                                            <div className="absolute top-3 right-3">
                                                <CheckCircle2 className="w-5 h-5 text-teal-500" />
                                            </div>
                                        )}
                                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-3`}>
                                            <type.icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h4 className="text-base font-semibold text-white mb-1">{type.name}</h4>
                                        <p className="text-xs text-slate-400">{type.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Company Details */}
                    {currentStep === 2 && (
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Company Details */}
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-teal-500" /> Company Info
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Company Name *</label>
                                        {/* <input
                                            type="text"
                                            value={formData.company_name}
                                            onChange={(e) => updateFormData('company_name', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Acme Logistics Inc."
                                        /> */}
                                        <ValidateInput
                                            value={formData.companyName}
                                            onChange={(value) =>
                                                updateFormData("companyName", value)
                                            }
                                            placeholder="Your company name"
                                            minLength={3}
                                            debounceTime={600}
                                            inputClassName="bg-slate-800 border-slate-600 text-white focus:ring-teal-500"
                                            validator={validateCompanyName}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Company Email *</label>

                                        <ValidateInput
                                            type="email"
                                            value={formData.profileEmailId}
                                            onChange={(value) =>
                                                updateFormData("profileEmailId", value)
                                            }
                                            placeholder="info@company.com"
                                            minLength={5}
                                            debounceTime={600}
                                            inputClassName="bg-slate-800 border-slate-600 text-white focus:ring-teal-500"
                                            validator={validateEmail}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Phone</label>
                                            <input
                                                type="tel"
                                                value={formData.profilePhone}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profilePhone",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="9876543210"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Website</label>
                                            <input
                                                type="url"
                                                value={formData.website}
                                                onChange={(e) =>
                                                    updateFormData("website", e.target.value)
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="https://company.com"
                                            />
                                        </div>
                                    </div>
                                    {/* Address 1 */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Address Line 1
                                        </label>

                                        <input
                                            type="text"
                                            value={formData.profileAddress1}
                                            onChange={(e) =>
                                                updateFormData(
                                                    "profileAddress1",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Street address"
                                        />
                                    </div>
                                    {/* Address 2 */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Address Line 2
                                        </label>

                                        <input
                                            type="text"
                                            value={formData.profileAddress2}
                                            onChange={(e) =>
                                                updateFormData(
                                                    "profileAddress2",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Apartment, suite, etc."
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Country
                                            </label>

                                            <select
                                                value={formData.profileCountry}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const match =
                                                        value.match(/\((.*?)\)/);

                                                    const countryCode =
                                                        match?.[1] || "";

                                                    updateFormData(
                                                        "profileCountry",
                                                        value
                                                    );

                                                    updateFormData(
                                                        "profileCountryCode",
                                                        countryCode
                                                    );

                                                    // call state api
                                                    fetchStates(countryCode);
                                                }
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            >
                                                <option value="">
                                                    Select Country
                                                </option>

                                                {countryList?.map((country) => (
                                                    <option
                                                        key={country.id}
                                                        value={country.incoterm}
                                                    >
                                                        {country.incoterm}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Region / State
                                            </label>

                                            <select
                                                value={formData.profileRegion}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profileRegion",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            >
                                                <option value="">
                                                    Select State
                                                </option>

                                                {stateList?.map((state) => (
                                                    <option
                                                        key={state.id}
                                                        value={state.stateName}
                                                    >
                                                        {state.stateName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                City
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.profileCity}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profileCity",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="Los Angeles"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Zip Code
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.profileZipCode}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profileZipCode",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="90001"
                                            />
                                        </div>

                                    </div>

                                    {/* Company Logo */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Company Logo
                                        </label>

                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) =>
                                                updateFormData(
                                                    "FileData",
                                                    e.target.files?.[0] || null
                                                )
                                            }
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm file:bg-teal-500 file:border-0 file:text-white file:px-3 file:py-1 file:rounded-md"
                                        />
                                    </div>
                                    {/* NVOCC License */}
                                    {formData.company_type === "NVOCC" && (
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                NVOCC License *
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.nvocc_license}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "nvocc_license",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="FMC License Number"
                                            />
                                        </div>
                                    )}
                                    {/* Status */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Initial Status
                                        </label>

                                        <select
                                            value={formData.subscription_status}
                                            onChange={(e) =>
                                                updateFormData(
                                                    "subscription_status",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        >
                                            <option value="active">Active</option>
                                            <option value="trial">Trial</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Admin User */}
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-teal-500" /> Admin Account
                                </h3>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                First Name
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.profileFirstName}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profileFirstName",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                                placeholder="John"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Last Name
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.profileLastName}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profileLastName",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>

                                    {/* Username */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Username *
                                        </label>

                                        <ValidateInput
                                            value={formData.profileUsername}
                                            onChange={(value) =>
                                                updateFormData("profileUsername", value)
                                            }
                                            placeholder="company username"
                                            minLength={4}
                                            debounceTime={600}
                                            inputClassName="bg-slate-800 border-slate-600 text-white focus:ring-teal-500"
                                            validator={validateUsername}
                                        />
                                    </div>
                                    {/* Password */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Password *
                                        </label>

                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={formData.profilePassword}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "profilePassword",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full px-3 py-2 pr-10 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="Minimum 8 characters"
                                            />

                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="w-4 h-4" />
                                                ) : (
                                                    <Eye className="w-4 h-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">
                                            Confirm Password *
                                        </label>

                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={formData.confirmPassword}
                                                onChange={(e) =>
                                                    updateFormData(
                                                        "confirmPassword",
                                                        e.target.value
                                                    )
                                                }
                                                className={`w-full px-3 py-2 pr-10 bg-slate-800 border rounded-lg text-white text-sm focus:outline-none focus:ring-2
            ${passwordError
                                                        ? "border-red-500 focus:ring-red-500"
                                                        : "border-slate-600 focus:ring-teal-500"
                                                    }`}
                                                placeholder="Confirm password"
                                            />

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowConfirmPassword(!showConfirmPassword)
                                                }
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff className="w-4 h-4" />
                                                ) : (
                                                    <Eye className="w-4 h-4" />
                                                )}
                                            </button>
                                        </div>

                                        {passwordError && (
                                            <p className="text-red-400 text-xs mt-1">
                                                {passwordError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Module Selection */}
                    {currentStep === 3 && (
                        <div className="space-y-8">
                            {/* HEADER */}
                            <div>
                                <h3 className="text-2xl font-bold text-white">
                                    Select Modules
                                </h3>

                                <p className="text-slate-400 text-sm mt-1">
                                    Choose core modules, enable specific features,
                                    and customize sub-modules for this company.
                                </p>
                            </div>

                            {(() => {
                                const allModules = modules || [];

                                const coreModules = allModules.filter(
                                    (item) => item.baseModuleType === "CORE"
                                );

                                const addonModules = allModules.filter(
                                    (item) => item.baseModuleType === "ADDON"
                                );

                                const selectedCoreModuleId = Object.keys(
                                    formData.selected_modules || {}
                                ).find((id) => {
                                    const module = coreModules.find(
                                        (m) => String(m.baseModuleId) === String(id)
                                    );

                                    return !!module;
                                });

                                const renderModuleCard = (
                                    module,
                                    type = "core"
                                ) => {
                                    const isSelected =
                                        !!formData.selected_modules?.[
                                        module.baseModuleId
                                        ];

                                    const isCore =
                                        module.baseModuleType === "CORE";

                                    const Icon =
                                        MODULE_ICONS[
                                        module.baseModuleName
                                            ?.replace(/\s+/g, "_")
                                            ?.toUpperCase()
                                        ] || Package;

                                    const theme =
                                        type === "core"
                                            ? {
                                                border: "border-teal-500",
                                                bg: "bg-teal-500/10",
                                                button:
                                                    "bg-teal-500 hover:bg-teal-600",
                                                text: "text-teal-400",
                                                soft: "bg-teal-500/20",
                                            }
                                            : {
                                                border: "border-purple-500",
                                                bg: "bg-purple-500/10",
                                                button:
                                                    "bg-purple-500 hover:bg-purple-600",
                                                text: "text-purple-400",
                                                soft: "bg-purple-500/20",
                                            };

                                    return (
                                        <div
                                            key={module.baseModuleId}
                                            className={`
            rounded-2xl border overflow-hidden
            transition-all duration-300 ease-in-out

            ${type === "core"
                                                    ? isSelected
                                                        ? "xl:col-span-2"
                                                        : selectedCoreModuleId
                                                            ? "hidden"
                                                            : "xl:col-span-1"
                                                    : "xl:col-span-1"
                                                }

            ${isSelected
                                                    ? `${theme.border} ${theme.bg}`
                                                    : "border-slate-700 bg-slate-900/60 hover:border-slate-500"
                                                }
        `}
                                        >
                                            {/* TOP CARD */}
                                            <div
                                                onClick={() => toggleBaseModule(module)}
                                                className="p-5 cursor-pointer"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex gap-4 flex-1">
                                                        {/* ICON */}
                                                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-slate-800">
                                                            <Icon className={`w-7 h-7 ${theme.text}`} />
                                                        </div>

                                                        {/* CONTENT */}
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <h4 className="text-lg font-semibold text-white">
                                                                    {module.baseModuleName}
                                                                </h4>

                                                                {isCore && (
                                                                    <span className="text-[10px] px-2 py-1 rounded-full bg-teal-500 text-white uppercase tracking-wide">
                                                                        Required
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                                                                {module.baseModuleDescription}
                                                            </p>

                                                            {/* PRICE */}
                                                            <div className="mt-4 flex items-end gap-1">
                                                                <span className="text-3xl font-bold text-white">
                                                                    ${module.baseModuleBasePrice}
                                                                </span>

                                                                <span className="text-sm text-slate-400 mb-1">
                                                                    /month
                                                                </span>
                                                            </div>

                                                            {/* COUNTS */}
                                                            {module.modulesData?.length > 0 && (
                                                                <div className="mt-3 flex gap-2 flex-wrap">
                                                                    <span
                                                                        className={`text-xs px-2 py-1 rounded-full ${theme.soft} ${theme.text}`}
                                                                    >
                                                                        {module.modulesData.length} Modules
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* CHECKBOX */}
                                                    <div
                                                        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                                                            ? `${theme.button} border-transparent`
                                                            : "border-slate-500"
                                                            }`}
                                                    >
                                                        {isSelected && (
                                                            <CheckCircle2 className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* EXPANDED MODULES */}
                                            {isSelected && module.modulesData?.length > 0 && (
                                                <div className="border-t border-slate-700 bg-slate-950/40 p-5">
                                                    {/* HEADER */}
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div>
                                                            <h5 className="text-white font-semibold">
                                                                Included Modules
                                                            </h5>

                                                            <p className="text-xs text-slate-400 mt-1">
                                                                Select all or customize individual modules
                                                            </p>
                                                        </div>

                                                        {/* SELECT ALL */}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAllChildModules(module)}
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${theme.button} text-white`}
                                                        >
                                                            Select All
                                                        </button>
                                                    </div>

                                                    {/* CHILD MODULE GRID */}
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {module.modulesData.map((child) => {
                                                            const childSelected =
                                                                !!formData.selected_modules?.[
                                                                    module.baseModuleId
                                                                ]?.modules?.[child.moduleId];

                                                            return (
                                                                <div
                                                                    key={child.moduleId}
                                                                    className={`rounded-xl border p-4 transition-all ${childSelected
                                                                        ? "border-teal-500 bg-teal-500/10"
                                                                        : "border-slate-700 bg-slate-900"
                                                                        }`}
                                                                >
                                                                    {/* CHILD HEADER */}
                                                                    <div
                                                                        onClick={() =>
                                                                            toggleChildModule(
                                                                                module.baseModuleId,
                                                                                child
                                                                            )
                                                                        }
                                                                        className="cursor-pointer"
                                                                    >
                                                                        <div className="flex justify-between items-start gap-3">
                                                                            <div className="flex-1">
                                                                                <h6 className="text-white font-semibold">
                                                                                    {child.moduleName}
                                                                                </h6>

                                                                                <p className="text-xs text-slate-400 mt-1">
                                                                                    Module access and workflow
                                                                                    management
                                                                                </p>

                                                                                <div className="mt-3 flex items-end gap-1">
                                                                                    <span className="text-xl font-bold text-white">
                                                                                        ${child.moduleBasePrice}
                                                                                    </span>

                                                                                    <span className="text-xs text-slate-400 mb-1">
                                                                                        /mo
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div
                                                                                className={`w-6 h-6 rounded-full border flex items-center justify-center ${childSelected
                                                                                    ? "bg-teal-500 border-teal-500"
                                                                                    : "border-slate-500"
                                                                                    }`}
                                                                            >
                                                                                {childSelected && (
                                                                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* SUBMODULES */}
                                                                    {childSelected &&
                                                                        child.subModulesData?.length > 0 && (
                                                                            <div className="mt-5 pt-4 border-t border-slate-700">
                                                                                <div className="flex items-center justify-between mb-3">
                                                                                    <div>
                                                                                        <p className="text-sm font-medium text-white">
                                                                                            Sub Modules
                                                                                        </p>

                                                                                        <p className="text-xs text-slate-400">
                                                                                            Choose required services
                                                                                        </p>
                                                                                    </div>

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            toggleAllSubModules(
                                                                                                module.baseModuleId,
                                                                                                child
                                                                                            )
                                                                                        }
                                                                                        className="text-xs px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-all"
                                                                                    >
                                                                                        Select All
                                                                                    </button>
                                                                                </div>

                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {child.subModulesData.map(
                                                                                        (sub) => {
                                                                                            const subSelected =
                                                                                                !!formData
                                                                                                    .selected_modules?.[
                                                                                                    module
                                                                                                        .baseModuleId
                                                                                                ]?.modules?.[
                                                                                                    child.moduleId
                                                                                                ]?.subModules?.[
                                                                                                sub.subModuleId
                                                                                                ];

                                                                                            return (
                                                                                                <button
                                                                                                    key={
                                                                                                        sub.subModuleId
                                                                                                    }
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        toggleSubModule(
                                                                                                            module.baseModuleId,
                                                                                                            child.moduleId,
                                                                                                            sub
                                                                                                        )
                                                                                                    }
                                                                                                    className={`px-4 py-2 rounded-xl text-sm border transition-all ${subSelected
                                                                                                        ? "bg-teal-500/20 border-teal-500 text-teal-300"
                                                                                                        : "border-slate-600 text-slate-300 hover:border-slate-500"
                                                                                                        }`}
                                                                                                >
                                                                                                    <div className="font-medium">
                                                                                                        {
                                                                                                            sub.subModuleName
                                                                                                        }
                                                                                                    </div>

                                                                                                    <div className="text-[10px] mt-1 opacity-80">
                                                                                                        $
                                                                                                        {
                                                                                                            sub.subModuleBasePrice
                                                                                                        }
                                                                                                        /mo
                                                                                                    </div>
                                                                                                </button>
                                                                                            );
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        {/* CORE */}
                                        <div>
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h4 className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                                                        Core Modules
                                                    </h4>

                                                    <p className="text-xs text-slate-400 mt-1">
                                                        Essential platform modules
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid xl:grid-cols-2 gap-5">
                                                {coreModules.map((module) =>
                                                    renderModuleCard(
                                                        module,
                                                        "core"
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {/* ADDONS */}
                                        <div>
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">
                                                        Add-on Modules
                                                    </h4>

                                                    <p className="text-xs text-slate-400 mt-1">
                                                        Optional premium integrations
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid xl:grid-cols-2 gap-5">
                                                {addonModules.map((module) =>
                                                    renderModuleCard(
                                                        module,
                                                        "addon"
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {/* TOTAL SUMMARY */}
                                        <div>
                                            <div className="rounded-2xl border border-slate-700 bg-slate-900/95 backdrop-blur-xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-2xl">
                                                <div className="flex gap-8">
                                                    <div>
                                                        <p className="text-xs text-slate-400 uppercase tracking-wide">
                                                            Base Modules
                                                        </p>

                                                        <p className="text-2xl font-bold text-white mt-1">
                                                            {
                                                                Object.keys(
                                                                    formData.selected_modules ||
                                                                    {}
                                                                ).length
                                                            }
                                                        </p>
                                                    </div>

                                                    <div>
                                                        <p className="text-xs text-slate-400 uppercase tracking-wide">
                                                            Total Monthly
                                                        </p>

                                                        <p className="text-3xl font-bold text-teal-400 mt-1">
                                                            $
                                                            {calculateTotal().toFixed(
                                                                2
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="text-sm text-slate-400">
                                                    Pricing updates automatically based on
                                                    selected modules and services.
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    {/* Step 4: Review */}
                    {currentStep === 4 && (() => {

                        const selectedBaseModules = Object.values(
                            formData.selected_modules || {}
                        );

                        return (
                            <div className="grid xl:grid-cols-3 gap-6">

                                {/* LEFT SIDE */}
                                <div className="xl:col-span-2 space-y-5">

                                    {/* COMPANY INFO */}
                                    <div className="p-5 rounded-2xl border border-slate-700 bg-slate-900">
                                        <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                                            Company Information
                                        </h4>

                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-xs text-slate-500">
                                                    Company Name
                                                </p>

                                                <p className="text-white font-semibold">
                                                    {formData.companyName || "-"}
                                                </p>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-xs text-slate-500">
                                                        Email
                                                    </p>

                                                    <p className="text-white">
                                                        {formData.profileEmailId || "-"}
                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-slate-500">
                                                        Phone
                                                    </p>

                                                    <p className="text-white">
                                                        {formData.profilePhone || "-"}
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-xs text-slate-500">
                                                    Address
                                                </p>

                                                <p className="text-white">
                                                    {formData.profileAddress1 || "-"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* MODULE SUMMARY */}
                                    <div className="p-5 rounded-2xl border border-slate-700 bg-slate-900">
                                        <div className="flex items-center justify-between mb-5">
                                            <div>
                                                <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                                                    Selected Modules
                                                </h4>

                                                <p className="text-xs text-slate-500 mt-1">
                                                    Review selected base modules, child modules,
                                                    and submodules
                                                </p>
                                            </div>

                                            <div className="px-3 py-1 rounded-lg bg-teal-500/10 text-teal-400 text-sm font-medium">
                                                {selectedBaseModules.length} Base Modules
                                            </div>
                                        </div>

                                        <div className="space-y-5">

                                            {selectedBaseModules.map((baseModule) => {

                                                const originalBase = modules.find(
                                                    (m) =>
                                                        String(m.baseModuleId) ===
                                                        String(baseModule.BASEMODULE_ID || baseModule.baseModuleId)
                                                );

                                                if (!originalBase) return null;

                                                const childModules = Object.values(
                                                    baseModule.modules || {}
                                                );

                                                return (
                                                    <div
                                                        key={originalBase.baseModuleId}
                                                        className="rounded-2xl border border-slate-700 bg-slate-950/50 overflow-hidden"
                                                    >

                                                        {/* BASE MODULE */}
                                                        <div className="p-5 border-b border-slate-700">
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <h5 className="text-lg font-semibold text-white">
                                                                        {originalBase.baseModuleName}
                                                                    </h5>

                                                                    <p className="text-sm text-slate-400 mt-1">
                                                                        {
                                                                            originalBase.baseModuleDescription
                                                                        }
                                                                    </p>
                                                                </div>

                                                                <div className="text-right">
                                                                    <p className="text-xs text-slate-500">
                                                                        Base Price
                                                                    </p>

                                                                    <p className="text-2xl font-bold text-teal-400">
                                                                        $
                                                                        {
                                                                            originalBase.baseModuleBasePrice
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* CHILD MODULES */}
                                                        <div className="p-5 space-y-4">

                                                            {childModules.length === 0 ? (
                                                                <div className="text-sm text-slate-500">
                                                                    No child modules selected
                                                                </div>
                                                            ) : (
                                                                childModules.map((child) => {

                                                                    const originalChild =
                                                                        originalBase.modulesData?.find(
                                                                            (m) =>
                                                                                String(m.moduleId) ===
                                                                                String(
                                                                                    child.MODULE_ID ||
                                                                                    child.moduleId
                                                                                )
                                                                        );

                                                                    if (!originalChild) return null;

                                                                    const subModules =
                                                                        Object.values(
                                                                            child.subModules || {}
                                                                        );

                                                                    return (
                                                                        <div
                                                                            key={originalChild.moduleId}
                                                                            className="rounded-xl border border-slate-700 bg-slate-900 p-4"
                                                                        >

                                                                            {/* CHILD HEADER */}
                                                                            <div className="flex items-center justify-between">
                                                                                <div>
                                                                                    <h6 className="font-semibold text-white">
                                                                                        {
                                                                                            originalChild.moduleName
                                                                                        }
                                                                                    </h6>

                                                                                    <p className="text-xs text-slate-500 mt-1">
                                                                                        Module
                                                                                    </p>
                                                                                </div>

                                                                                <div className="text-right">
                                                                                    <p className="text-lg font-bold text-white">
                                                                                        $
                                                                                        {
                                                                                            originalChild.moduleBasePrice
                                                                                        }
                                                                                    </p>

                                                                                    <p className="text-xs text-slate-500">
                                                                                        /month
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            {/* SUBMODULES */}
                                                                            {subModules.length > 0 && (
                                                                                <div className="mt-4 pt-4 border-t border-slate-700">

                                                                                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">
                                                                                        Sub Modules
                                                                                    </p>

                                                                                    <div className="flex flex-wrap gap-2">

                                                                                        {subModules.map(
                                                                                            (sub) => {

                                                                                                const originalSub =
                                                                                                    originalChild.subModulesData?.find(
                                                                                                        (s) =>
                                                                                                            String(
                                                                                                                s.subModuleId
                                                                                                            ) ===
                                                                                                            String(
                                                                                                                sub.SUBMODULE_ID ||
                                                                                                                sub.subModuleId
                                                                                                            )
                                                                                                    );

                                                                                                if (!originalSub)
                                                                                                    return null;

                                                                                                return (
                                                                                                    <div
                                                                                                        key={
                                                                                                            originalSub.subModuleId
                                                                                                        }
                                                                                                        className="px-4 py-2 rounded-xl bg-teal-500/10 border border-teal-500/20"
                                                                                                    >
                                                                                                        <div className="text-sm font-medium text-teal-300">
                                                                                                            {
                                                                                                                originalSub.subModuleName
                                                                                                            }
                                                                                                        </div>

                                                                                                        <div className="text-[11px] text-slate-400 mt-1">
                                                                                                            $
                                                                                                            {
                                                                                                                originalSub.subModuleBasePrice
                                                                                                            }
                                                                                                            /mo
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );
                                                                                            }
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* BILLING */}
                                <div>
                                    <div className="sticky top-5 rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-950 p-6">

                                        <h4 className="text-xl font-bold text-white mb-5">
                                            Billing Summary
                                        </h4>

                                        <div className="space-y-3">

                                            {selectedBaseModules.map((baseModule) => {

                                                const originalBase = modules.find(
                                                    (m) =>
                                                        String(m.baseModuleId) ===
                                                        String(baseModule.BASEMODULE_ID || baseModule.baseModuleId)
                                                );

                                                if (!originalBase) return null;

                                                return (
                                                    <div
                                                        key={originalBase.baseModuleId}
                                                        className="flex items-center justify-between"
                                                    >
                                                        <span className="text-slate-300 text-sm">
                                                            {originalBase.baseModuleName}
                                                        </span>

                                                        <span className="text-white font-medium">
                                                            $
                                                            {
                                                                originalBase.baseModuleBasePrice
                                                            }
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="my-5 border-t border-slate-700" />

                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-400">
                                                Total Monthly
                                            </span>

                                            <span className="text-3xl font-bold text-teal-400">
                                                $
                                                {calculateTotal().toFixed(2)}
                                            </span>
                                        </div>

                                        <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                                            Pricing updates automatically based on selected
                                            modules, child modules, and submodules.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Footer */}
                <div className="bg-slate-800 border-t border-slate-700 px-6 py-4 flex justify-between">
                    <button
                        onClick={currentStep === 1 ? onClose : prevStep}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> {currentStep === 1 ? 'Cancel' : 'Back'}
                    </button>

                    {currentStep < 4 ? (
                        <button
                            onClick={nextStep}
                            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleCreateCompany}
                            disabled={loading}
                            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Create Company
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyWizard;
